import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Badge, { readinessVariant } from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';
import ReadinessModal from '../components/ui/ReadinessModal';
import HospitalFacilityService from '../services/HospitalFacilityService';
import ReadinessLevelService from '../services/ReadinessLevelService';

const EMPTY_FORM = { name: '', code: '', bedCapacity: '', isActive: true };

// Deliberately minimal — identity, bed capacity and readiness only. FHIR/HIS integration
// config (fhirBaseUrl/authType/authConfig) stays HospitalFacilityController's separate
// concern and isn't exposed here.
export default function Hospitals() {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readinessLevelIds, setReadinessLevelIds] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);

  const [readinessRow, setReadinessRow] = useState(null);
  const [readinessBusy, setReadinessBusy] = useState(false);

  const loadHospitals = async () => {
    try {
      const response = await HospitalFacilityService.findAll();
      if (response?.status === 200) setHospitals(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const loadReadinessLevels = async () => {
    try {
      const response = await ReadinessLevelService.findAll();
      if (response?.status === 200) {
        const map = {};
        response.data.forEach((rl) => { map[rl.level] = rl.id; });
        setReadinessLevelIds(map);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => { loadHospitals(); loadReadinessLevels(); }, []);

  const openCreate = () => {
    setEditId(null);
    setFormData(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setFormData({ name: row.name, code: row.code || '', bedCapacity: row.bedCapacity ?? '', isActive: row.isActive });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.info('Please enter a hospital name');
      return;
    }
    const payload = { ...formData, bedCapacity: formData.bedCapacity === '' ? null : Number(formData.bedCapacity) };
    try {
      if (editId) {
        await HospitalFacilityService.update(editId, payload);
        toast.success('Hospital updated successfully');
      } else {
        await HospitalFacilityService.create(payload);
        toast.success('Hospital added successfully');
      }
      setFormOpen(false);
      loadHospitals();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save hospital');
    }
  };

  const openReadiness = (row) => setReadinessRow(row);
  const closeReadiness = () => setReadinessRow(null);

  const handleSetReadiness = async (level) => {
    const readinessLevelId = readinessLevelIds[level];
    if (!readinessLevelId) return;
    setReadinessBusy(true);
    try {
      await HospitalFacilityService.update(readinessRow.id, { readinessLevelId });
      toast.success(`Readiness set to ${level}`);
      closeReadiness();
      loadHospitals();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update readiness level');
    } finally {
      setReadinessBusy(false);
    }
  };

  const headCells = [
    { id: 'name', label: 'Name' },
    { id: 'code', label: 'Code', render: (row) => row.code || '—' },
    { id: 'bedCapacity', label: 'Bed Capacity', render: (row) => row.bedCapacity ?? '—' },
    {
      id: 'isActive',
      label: 'Status',
      render: (row) => <Badge variant={row.isActive ? 'green' : 'neutral'}>{row.isActive ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      id: 'readinessLevel',
      label: 'Readiness',
      render: (row) => {
        const level = row.readiness_level?.level || 'Normal';
        return <Badge variant={readinessVariant(level)}>{level}</Badge>;
      },
    },
  ];

  const options = [{ title: 'Manage Readiness', handler: (row) => openReadiness(row) }];

  return (
    <Sidebar title="Hospitals">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-500 dark:text-neutral-500">Hospitals participating in the CBRNE response network.</p>
          <Button icon={Plus} onClick={openCreate}>New Hospital</Button>
        </div>

        <DataTable
          title="Hospitals"
          rows={hospitals}
          headCells={headCells}
          moreOptions={options}
          onEdit={openEdit}
          loading={loading}
          emptyLabel={loading ? 'Loading hospitals…' : 'No hospitals found.'}
        />
      </div>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? 'Edit Hospital' : 'New Hospital'}
        onConfirm={handleSave}
        confirmLabel="Save"
      >
        <div className="space-y-4">
          <div>
            <label className="form-label">Name</label>
            <input
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">Code</label>
            <input
              className="form-input"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">Bed Capacity</label>
            <input
              type="number"
              min="0"
              className="form-input"
              value={formData.bedCapacity}
              onChange={(e) => setFormData({ ...formData, bedCapacity: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Active</span>
          </label>
        </div>
      </Modal>

      <ReadinessModal
        open={Boolean(readinessRow)}
        subjectLabel={readinessRow?.name}
        currentLevel={readinessRow?.readiness_level?.level}
        onClose={closeReadiness}
        onSelect={handleSetReadiness}
        busy={readinessBusy}
      />
    </Sidebar>
  );
}
